import React from "react";
import { useTranslation } from "react-i18next";
import { Award, FileText, Sparkles, Trophy } from "lucide-react";

export default function ProfileProgressionStats({ progressionView }) {
  const { t } = useTranslation();
  const {
    showPrivateDetails,
    badgeSrc,
    nextBadgeSrc,
    formattedXp,
    formattedCredits,
    formattedGlobalRank,
    formattedLevelStartXp,
    formattedCurrentXp,
    formattedGoalXp,
    formattedNextLevelXpTarget,
    formattedProgress,
    formattedRemainingXp,
    currentRankName,
    nextRankName,
    nextLevelXpTarget,
    progressPercent,
    openInfoModal,
  } = progressionView;

  return (
    <>
      <div
        className={`profile-progression-stats ${
          showPrivateDetails ? "" : "profile-progression-stats--summary"
        }`.trim()}
      >
        {showPrivateDetails ? (
          <button
            type="button"
            className="profile-progression-stat profile-progression-stat--interactive profile-progression-stat--xp"
            onClick={() => openInfoModal("xp")}
            aria-label={t("progression_xp_button_label", {
              defaultValue: "Open experience points information",
            })}
          >
            <span className="profile-progression-stat-icon" aria-hidden="true">
              <Sparkles size={18} strokeWidth={2.2} />
            </span>
            <span className="profile-progression-stat-copy">
              <span className="profile-progression-stat-label">
                {t("progression_xp_label", { defaultValue: "XP" })}
              </span>
              <strong className="profile-progression-stat-value">
                {formattedXp}
              </strong>
            </span>
          </button>
        ) : (
          <div className="profile-progression-stat">
            <span className="profile-progression-stat-label">
              {t("progression_xp_label", { defaultValue: "XP" })}
            </span>
            <strong className="profile-progression-stat-value">
              {formattedXp}
            </strong>
          </div>
        )}

        {showPrivateDetails ? (
          <>
            <button
              type="button"
              className="profile-progression-stat profile-progression-stat--interactive profile-progression-stat--mobile-only profile-progression-stat--level"
              onClick={() => openInfoModal("level")}
              aria-label={t("progression_level_button_label")}
            >
              <span className="profile-progression-stat-icon" aria-hidden="true">
                <Award size={18} strokeWidth={2.2} />
              </span>
              <span className="profile-progression-stat-copy">
                <span className="profile-progression-stat-label">
                  {t("progression_level_label_short", { defaultValue: "Level" })}
                </span>
                <strong className="profile-progression-stat-value">
                  {currentRankName}
                </strong>
              </span>
            </button>

            <button
              type="button"
              className="profile-progression-stat profile-progression-stat--interactive profile-progression-stat--mobile-only profile-progression-stat--rank"
              onClick={() => openInfoModal("rank")}
              aria-label={t("progression_rank_button_label")}
            >
              <span className="profile-progression-stat-icon" aria-hidden="true">
                <Trophy size={18} strokeWidth={2.2} />
              </span>
              <span className="profile-progression-stat-copy">
                <span className="profile-progression-stat-label">
                  {t("profile_global_rank_label", { defaultValue: "Rank" })}
                </span>
                <strong className="profile-progression-stat-value">
                  {formattedGlobalRank}
                </strong>
              </span>
            </button>

            <button
              type="button"
              className="profile-progression-stat profile-progression-stat--interactive profile-progression-stat--mobile-only profile-progression-stat--bio"
              onClick={() => openInfoModal("bio")}
              aria-label={t("profile_bio_button_label", {
                defaultValue: "Bio",
              })}
            >
              <span className="profile-progression-stat-icon" aria-hidden="true">
                <FileText size={18} strokeWidth={2.2} />
              </span>
              <span className="profile-progression-stat-copy">
                <span className="profile-progression-stat-label">
                  {t("profile_bio_button_label", { defaultValue: "Bio" })}
                </span>
              </span>
            </button>

            <div className="profile-progression-stat profile-progression-stat--target">
              <span className="profile-progression-stat-label">
                {t("progression_target_label", { defaultValue: "Next target" })}
              </span>
              <strong className="profile-progression-stat-value">
                {formattedNextLevelXpTarget ||
                  t("progression_max_label", { defaultValue: "Max level" })}
              </strong>
            </div>

            <div className="profile-progression-stat profile-progression-stat--credits profile-progression-stat--wide-mobile">
              <span className="profile-progression-stat-label">
                {t("progression_credits_label", { defaultValue: "Credits" })}
              </span>
              <strong className="profile-progression-stat-value">
                {formattedCredits}
              </strong>
            </div>
          </>
        ) : null}
      </div>

      {showPrivateDetails ? (
        <div className="profile-progression-badge-track">
          <div className="profile-mobile-section-heading">
            {t("profile_progress_title", {
              defaultValue: "Progress",
            })}
          </div>
          <div className="profile-progression-badge-track__content">
            {badgeSrc ? (
              <button
                type="button"
                className="profile-progression-badge-track__badge"
                onClick={() => openInfoModal("badge")}
              >
                <img
                  src={badgeSrc}
                  alt={t("progression_current_badge", {
                    defaultValue: "Current badge",
                  })}
                />
                <span>{currentRankName}</span>
              </button>
            ) : (
              <span className="profile-progression-badge-track__badge profile-progression-badge-track__badge--empty">
                <span className="profile-progression-badge-track__placeholder">
                  {t("progression_current_badge", {
                    defaultValue: "Current badge",
                  })}
                </span>
                <span>{currentRankName}</span>
              </span>
            )}

            <div className="profile-progression-badge-track__progress">
              <div
                className="profile-progression-progress-current-marker"
                style={{
                  left: `clamp(16px, ${progressPercent}%, calc(100% - 16px))`,
                }}
              >
                {formattedCurrentXp}
              </div>

              <div
                className="profile-progression-progress-track"
                aria-label={
                  nextLevelXpTarget
                    ? t("progression_progress_marker_label", {
                        defaultValue:
                          "{{start}} XP start, {{current}} XP current, {{goal}} XP goal",
                        start: formattedLevelStartXp,
                        current: formattedCurrentXp,
                        goal: formattedGoalXp,
                      })
                    : t("progression_max_level_note", {
                        defaultValue: "Maximum level reached",
                      })
                }
              >
                <span
                  className="profile-progression-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="profile-progression-progress-xp-markers">
                <span>{formattedLevelStartXp}</span>
                <span>
                  {formattedGoalXp ||
                    t("progression_max_label", { defaultValue: "Max level" })}
                </span>
              </div>
            </div>

            {nextRankName ? (
              <button
                type="button"
                className="profile-progression-badge-track__badge profile-progression-badge-track__badge--next"
                onClick={() => openInfoModal("next-badge")}
              >
                {nextBadgeSrc ? (
                  <img
                    src={nextBadgeSrc}
                    alt={t("progression_next_badge", {
                      defaultValue: "Next badge",
                    })}
                  />
                ) : (
                  <span className="profile-progression-badge-track__placeholder">
                    {t("progression_next_badge_placeholder", {
                      defaultValue: "Next",
                    })}
                  </span>
                )}
                <span>{nextRankName}</span>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {showPrivateDetails ? (
        <div className="profile-progression-progress profile-progression-progress--standard">
          <div className="profile-progression-progress-meta">
            <span className="profile-progression-progress-label">
              {t("progression_progress_label", {
                defaultValue: "Progress to next level",
              })}
            </span>

            <span className="profile-progression-progress-value">
              {formattedProgress ||
                t("progression_complete_label", {
                  defaultValue: "Complete",
                })}
            </span>
          </div>

          <div className="profile-progression-progress-track" aria-hidden="true">
            <span
              className="profile-progression-progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="profile-progression-progress-footnote">
            {nextLevelXpTarget
              ? t("progression_remaining_label", {
                  defaultValue: "{{xp}} XP to next level",
                  xp: formattedRemainingXp,
                })
              : t("progression_max_level_note", {
                  defaultValue: "Maximum level reached",
                })}
          </div>
        </div>
      ) : null}
    </>
  );
}
